import type { Root } from './prototypeTypes';
import { persistThemeMode } from './themeBootstrapRuntime';

const TRANSITION_RECORD_KEY = 'deepmox:ios-theme-transition';
const TRANSITION_NONCE_PARAM = '__dm_theme_tx';
const TRANSITION_RECORD_TTL_MS = 120000;
const BOUNCE_DURATION_MS = 1500;
const RADIAL_DURATION_MS = 850;
const REDUCED_COVER_DURATION_MS = 160;
const EVENT_GRACE_MS = 220;

interface IosThemeTransitionInput {
	mode: string;
	targetBackground: string;
	root: Root;
	onFallback: () => void;
}

interface IosThemeTransitionDependencies {
	persistMode: (mode: string) => boolean;
	replaceLocation: (url: string) => void;
	now: () => number;
}

interface ActiveTransition {
	settled: boolean;
	ball: HTMLElement | null;
	cover: HTMLElement | null;
	timerIds: number[];
	lockedControls: Array<{ element: HTMLButtonElement; disabled: boolean; ariaDisabled: string | null }>;
	recordCreated: boolean;
	onFallback: () => void;
	pageshowHandler: ((event: PageTransitionEvent) => void) | null;
}

let activeTransition: ActiveTransition | null = null;

function clearTimers(transaction: ActiveTransition): void {
	for (const timerId of transaction.timerIds) window.clearTimeout(timerId);
	transaction.timerIds = [];
}

function unlockThemeControls(transaction: ActiveTransition): void {
	for (const control of transaction.lockedControls) {
		control.element.disabled = control.disabled;
		if (control.ariaDisabled === null) control.element.removeAttribute('aria-disabled');
		else control.element.setAttribute('aria-disabled', control.ariaDisabled);
	}
	transaction.lockedControls = [];
}

function removeResources(transaction: ActiveTransition): void {
	clearTimers(transaction);
	if (transaction.pageshowHandler) window.removeEventListener('pageshow', transaction.pageshowHandler);
	transaction.pageshowHandler = null;
	transaction.ball?.remove();
	transaction.cover?.remove();
	transaction.ball = null;
	transaction.cover = null;
	document.documentElement.classList.remove('ios-theme-transaction-active');
	unlockThemeControls(transaction);
}

function removeTransitionRecord(): void {
	try {
		window.sessionStorage.removeItem(TRANSITION_RECORD_KEY);
	} catch {
		// A denied session store is already handled by the in-page fallback.
	}
}

function fallBack(transaction: ActiveTransition): void {
	if (transaction.settled || activeTransition !== transaction) return;
	transaction.settled = true;
	activeTransition = null;
	removeResources(transaction);
	if (transaction.recordCreated) removeTransitionRecord();
	transaction.onFallback();
}

function lockThemeControls(root: Root): ActiveTransition['lockedControls'] {
	return Array.from(root.querySelectorAll<HTMLButtonElement>('[data-theme-mode]')).map((element) => {
		const control = { element, disabled: element.disabled, ariaDisabled: element.getAttribute('aria-disabled') };
		element.disabled = true;
		element.setAttribute('aria-disabled', 'true');
		return control;
	});
}

function createNonce(): string {
	if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
	const bytes = new Uint32Array(4);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (value) => value.toString(36)).join('-');
}

/**
 * Appends the internal nonce parameter to the URL without re-encoding the
 * existing query string. URLSearchParams would normalise %20 to + and
 * re-order/re-encode all existing parameters, breaking signed links and
 * payment redirects that depend on the original byte-level encoding.
 */
function appendNonceToLocation(href: string, nonce: string): string {
	const hashIndex = href.indexOf('#');
	const beforeHash = hashIndex === -1 ? href : href.slice(0, hashIndex);
	const hash = hashIndex === -1 ? '' : href.slice(hashIndex);
	const questionIndex = beforeHash.indexOf('?');
	const separator = questionIndex === -1 ? '?' : beforeHash.endsWith('?') || beforeHash.endsWith('&') ? '' : '&';
	const encodedNonce = `${TRANSITION_NONCE_PARAM}=${encodeURIComponent(nonce)}`;
	return `${beforeHash}${separator}${encodedNonce}${hash}`;
}

function waitForAnimation(element: HTMLElement, durationMs: number, transaction: ActiveTransition, callback: () => void): void {
	let completed = false;
	const complete = () => {
		if (completed || transaction.settled || activeTransition !== transaction) return;
		completed = true;
		callback();
	};
	element.addEventListener('animationend', complete, { once: true });
	element.addEventListener('transitionend', complete, { once: true });
	transaction.timerIds.push(window.setTimeout(complete, durationMs + EVENT_GRACE_MS));
}

function createStableCover(targetBackground: string): HTMLElement {
	const cover = document.createElement('div');
	cover.className = 'ios-theme-navigation-cover';
	cover.style.backgroundColor = targetBackground;
	cover.setAttribute('aria-hidden', 'true');
	const progress = document.createElement('span');
	progress.className = 'ios-theme-navigation-progress';
	cover.appendChild(progress);
	return cover;
}

function scheduleNavigation(
	transaction: ActiveTransition,
	input: IosThemeTransitionInput,
	dependencies: IosThemeTransitionDependencies,
): void {
	if (transaction.settled || activeTransition !== transaction) return;
	try {
		const nonce = createNonce();
		const record = {
			nonce,
			requestedMode: input.mode,
			targetBackground: input.targetBackground,
			expiresAt: dependencies.now() + TRANSITION_RECORD_TTL_MS,
			consumed: false,
		};
		window.sessionStorage.setItem(TRANSITION_RECORD_KEY, JSON.stringify(record));
		transaction.recordCreated = true;
		// Preserve the original query string byte-for-byte. URLSearchParams re-encodes
		// the entire search string (e.g. %20 -> +), which breaks signed links, payment
		// redirects, and external deep links that depend on the original encoding.
		const targetUrl = appendNonceToLocation(window.location.href, nonce);
		dependencies.replaceLocation(targetUrl);
	} catch {
		fallBack(transaction);
	}
}

function showStableCover(
	transaction: ActiveTransition,
	input: IosThemeTransitionInput,
	dependencies: IosThemeTransitionDependencies,
	reducedMotion: boolean,
): void {
	if (transaction.settled || activeTransition !== transaction) return;
	const cover = createStableCover(input.targetBackground);
	if (reducedMotion) cover.classList.add('ios-theme-navigation-cover-reduced');
	document.body.appendChild(cover);
	transaction.cover = cover;
	void cover.offsetWidth;
	cover.classList.add('ios-theme-navigation-cover-visible');
	if (reducedMotion) {
		waitForAnimation(cover, REDUCED_COVER_DURATION_MS, transaction, () => scheduleNavigation(transaction, input, dependencies));
		return;
	}
	transaction.ball?.remove();
	transaction.ball = null;
	scheduleNavigation(transaction, input, dependencies);
}

function runRadialTransition(
	transaction: ActiveTransition,
	input: IosThemeTransitionInput,
	dependencies: IosThemeTransitionDependencies,
): void {
	const ball = transaction.ball;
	if (!ball || transaction.settled || activeTransition !== transaction) return;
	const rect = ball.getBoundingClientRect();
	const centerX = rect.left + rect.width / 2;
	const centerY = rect.top + rect.height / 2;
	const radius = Math.hypot(Math.max(centerX, window.innerWidth - centerX), Math.max(centerY, window.innerHeight - centerY));
	ball.style.setProperty('--ios-theme-cover-scale', String(Math.max(1, radius / (rect.width / 2))));
	ball.classList.remove('theme-ball-bounce');
	ball.classList.add('ios-theme-cover-expand');
	waitForAnimation(ball, RADIAL_DURATION_MS, transaction, () => showStableCover(transaction, input, dependencies, false));
}

function startNormalMotion(
	transaction: ActiveTransition,
	input: IosThemeTransitionInput,
	dependencies: IosThemeTransitionDependencies,
): void {
	const ball = document.createElement('div');
	ball.className = 'theme-ball';
	ball.style.backgroundColor = input.targetBackground;
	document.body.appendChild(ball);
	transaction.ball = ball;
	void ball.offsetWidth;
	ball.classList.add('theme-ball-bounce');
	waitForAnimation(ball, BOUNCE_DURATION_MS, transaction, () => runRadialTransition(transaction, input, dependencies));
}

/**
 * Starts the iOS-only full-document theme transaction. The old document's
 * theme is intentionally left untouched until navigation succeeds or the
 * transaction fails back to the regular in-page theme reveal.
 */
export function runIosThemeTransition(
	input: IosThemeTransitionInput,
	dependencies: IosThemeTransitionDependencies = {
		persistMode: persistThemeMode,
		replaceLocation: (url) => window.location.replace(url),
		now: () => Date.now(),
	},
): boolean {
	if (activeTransition) return true;
	if (!dependencies.persistMode(input.mode)) return false;

	const transaction: ActiveTransition = {
		settled: false,
		ball: null,
		cover: null,
		timerIds: [],
		lockedControls: lockThemeControls(input.root),
		recordCreated: false,
		onFallback: input.onFallback,
		pageshowHandler: null,
	};
	activeTransition = transaction;
	document.documentElement.classList.add('ios-theme-transaction-active');
	transaction.pageshowHandler = (event) => {
		if (!event.persisted || activeTransition !== transaction) return;
		transaction.settled = true;
		activeTransition = null;
		removeResources(transaction);
		if (transaction.recordCreated) removeTransitionRecord();
	};
	window.addEventListener('pageshow', transaction.pageshowHandler);

	try {
		const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
		if (reducedMotion) showStableCover(transaction, input, dependencies, true);
		else startNormalMotion(transaction, input, dependencies);
		return true;
	} catch {
		fallBack(transaction);
		return true;
	}
}
